import { chakra } from '@chakra-ui/react';
import { useId } from 'react';

/**
 * Complyance AI icon — three sparkle shapes filled with the AI gradient.
 * This is the canonical icon for all AI-powered features on the platform.
 * Use this instead of the generic SparklesIcon from Heroicons.
 */
export const AiIcon = chakra((props: React.SVGProps<SVGSVGElement>) => {
  // Unique per-instance gradient IDs — multiple AiIcons on the same page
  // would otherwise collide on duplicate SVG IDs and fail to render in some browsers.
  const uid = useId().replace(/:/g, '');
  const g0 = `ai-icon-gradient-0-${uid}`;
  const g1 = `ai-icon-gradient-1-${uid}`;
  const g2 = `ai-icon-gradient-2-${uid}`;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      {...props}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M9 4.5C9.33486 4.5 9.62915 4.72198 9.72114 5.04396L10.5343 7.89015C10.8903 9.13593 11.8641 10.1097 13.1099 10.4657L15.956 11.2789C16.278 11.3709 16.5 11.6651 16.5 12C16.5 12.3349 16.278 12.6291 15.956 12.7211L13.1098 13.5343C11.8641 13.8903 10.8903 14.8641 10.5343 16.1099L9.72114 18.956C9.62915 19.278 9.33486 19.5 9 19.5C8.66514 19.5 8.37085 19.278 8.27886 18.956L7.46566 16.1099C7.10972 14.8641 6.13593 13.8903 4.89015 13.5343L2.04396 12.7211C1.72198 12.6291 1.5 12.3349 1.5 12C1.5 11.6651 1.72198 11.3709 2.04396 11.2789L4.89015 10.4657C6.13593 10.1097 7.10972 9.13593 7.46566 7.89015L8.27886 5.04396C8.37085 4.72198 8.66514 4.5 9 4.5Z"
        fill={`url(#${g0})`}
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M18 1.5C18.3442 1.5 18.6441 1.73422 18.7276 2.0681L18.9865 3.10356C19.2216 4.04406 19.9559 4.7784 20.8964 5.01353L21.9319 5.27239C22.2658 5.35586 22.5 5.65585 22.5 6C22.5 6.34415 22.2658 6.64414 21.9319 6.72761L20.8964 6.98647C19.9559 7.2216 19.2216 7.95594 18.9865 8.89644L18.7276 9.9319C18.6441 10.2658 18.3442 10.5 18 10.5C17.6558 10.5 17.3559 10.2658 17.2724 9.9319L17.0135 8.89644C16.7784 7.95594 16.0441 7.2216 15.1036 6.98647L14.0681 6.72761C13.7342 6.64414 13.5 6.34415 13.5 6C13.5 5.65585 13.7342 5.35586 14.0681 5.27239L15.1036 5.01353C16.0441 4.7784 16.7784 4.04406 17.0135 3.10356L17.2724 2.0681C17.3559 1.73422 17.6558 1.5 18 1.5Z"
        fill={`url(#${g1})`}
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M16.5 15C16.8228 15 17.1094 15.2066 17.2115 15.5128L17.6058 16.6956C17.7551 17.1435 18.1065 17.4949 18.5544 17.6442L19.7372 18.0385C20.0434 18.1406 20.25 18.4272 20.25 18.75C20.25 19.0728 20.0434 19.3594 19.7372 19.4615L18.5544 19.8558C18.1065 20.0051 17.7551 20.3565 17.6058 20.8044L17.2115 21.9872C17.1094 22.2934 16.8228 22.5 16.5 22.5C16.1772 22.5 15.8906 22.2934 15.7885 21.9872L15.3942 20.8044C15.2449 20.3565 14.8935 20.0051 14.4456 19.8558L13.2628 19.4615C12.9566 19.3594 12.75 19.0728 12.75 18.75C12.75 18.4272 12.9566 18.1406 13.2628 18.0385L14.4456 17.6442C14.8935 17.4949 15.2449 17.1435 15.3942 16.6956L15.7885 15.5128C15.8906 15.2066 16.1772 15 16.5 15Z"
        fill={`url(#${g2})`}
      />
      <defs>
        <linearGradient id={g0} x1="2" y1="8.5" x2="23" y2="23" gradientUnits="userSpaceOnUse">
          <stop style={{ stopColor: 'var(--ck-ai-gradient-stop-start)' }} />
          <stop offset="1" style={{ stopColor: 'var(--ck-ai-gradient-stop-end)' }} />
        </linearGradient>
        <linearGradient id={g1} x1="2" y1="8.5" x2="23" y2="23" gradientUnits="userSpaceOnUse">
          <stop style={{ stopColor: 'var(--ck-ai-gradient-stop-start)' }} />
          <stop offset="1" style={{ stopColor: 'var(--ck-ai-gradient-stop-end)' }} />
        </linearGradient>
        <linearGradient id={g2} x1="2" y1="8.5" x2="23" y2="23" gradientUnits="userSpaceOnUse">
          <stop style={{ stopColor: 'var(--ck-ai-gradient-stop-start)' }} />
          <stop offset="1" style={{ stopColor: 'var(--ck-ai-gradient-stop-end)' }} />
        </linearGradient>
      </defs>
    </svg>
  );
});

AiIcon.displayName = 'AiIcon';
